import { ISyntheticEvent } from '@mecontrol/public-api';
import {
    ISyntheticEventTarget,
    MeControlEventType,
    EventDetailsMap,
    MeControlEventHandler,
    IMeControlEvent
} from '@mecontrol/common';

export class SyntheticEvent<Type extends MeControlEventType> implements ISyntheticEvent {
    private _defaultPrevented: boolean;

    get defaultPrevented(): boolean {
        return this._defaultPrevented;
    }

    constructor(public type: Type, public detail: EventDetailsMap[Type]) {
        this._defaultPrevented = false;
    }

    /**
     * preventDefault - when called by a registered event listener (see {@link addEventListener}) this effectively
     * tells the system that the event was "cancelled". If this is called by any listener while handling an event
     * that was dispatched by {@link dispatchEvent} it will cause a return value of false from that call to {@link dispatchEvent}
     * @returns void
     */
    public preventDefault(): void {
        this._defaultPrevented = true;
    }
}

type ListenersArray<Type extends MeControlEventType> = Array<MeControlEventHandler<Type>>;
type IEventListeners = { [key in MeControlEventType]?: ListenersArray<key> };

export class SyntheticEventTarget implements ISyntheticEventTarget {
    private _listeners: IEventListeners = {};

    /**
     * addEventListener - registers a listener for a particular named event
     * @param type The string name of the event that the listener will be listening for. All calls to dispatchEvent with this same name will result in a call to this listener with the event data presented.
     * @param callback The callback function to register. NOTE: if you want to remove later, create a real function and send a reference to it here - the remove uses simple equality to locate callbacks to remove.
     * @returns void
     */
    public addEventListener<Type extends MeControlEventType>(type: Type, callback: MeControlEventHandler<Type>): void {
        if (!(type in this._listeners)) {
            this._listeners[type] = [];
        }

        // TODO: Explore ways to remove this cast
        (this._listeners[type] as ListenersArray<Type>).push(callback);
    }

    /**
     * removeEventListener - removes a registered listener for a particular named event
     * @param type The string name of the event that the listener was listening for
     * @param callback The callback function that was previously registered (which will be removed). NOTE: the function uses simple reference equality to find the handler to remove.
     * @returns void
     */
    public removeEventListener<Type extends MeControlEventType>(type: Type, callback: MeControlEventHandler<Type>): void {
        if (!(type in this._listeners)) {
            return;
        }

        // TODO: Explore ways to remove this cast
        const listeners = this._listeners[type] as ListenersArray<Type>;
        const index = listeners.indexOf(callback);
        if (index !== -1) {
            listeners.splice(index, 1);
        }
    }

    /**
     * dispatchEvent - raises an event to all registered event listeners (see addEventListener for registering).
     * @param event The event to dispatch. The event should contain a 'type', and optionally can pass additional data in the 'detail' object.
     * @returns true if none of the registered listeners "cancelled" the event by calling event.preventDefault, false if any listener DID cancel the event by calling event.preventDefault
     */
    public dispatchEvent<Type extends MeControlEventType>(event: IMeControlEvent<Type>): boolean {

        if (!(event.type in this._listeners)) {
            return true;
        }

        // TODO Explore ways to remove this cast
        let stack = this._listeners[event.type] as ListenersArray<Type>;

        // build up a full event with the input...
        let dispatchedEvent = new SyntheticEvent(event.type, event.detail);

        for (let i = 0, l = stack.length; i < l; i++) {
            stack[i].call(this, dispatchedEvent);
        }

        return !dispatchedEvent.defaultPrevented;
    }
}
